'use client';

import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import {
  Container, Title, Text, Box, Group, Button, Stack, Paper, Badge,
  TextInput, Skeleton, ActionIcon, Modal, SegmentedControl
} from '@mantine/core';
import { IconCheck, IconX, IconArrowRight, IconFlag, IconSearch, IconPuzzle, IconFlame, IconStar, IconArrowBack, IconAlertTriangle, IconRefresh, IconEyeOff } from '@tabler/icons-react';
import Link from 'next/link';
import { fetchNextGroup, fetchNextUnmatched, approveGroup, reassignStep, flagInventory, reportUnclearSteps, fetchCommunityProgress, fetchLeaderboard, undoVotes, fetchMatchingInventory } from '@/lib/api';
import { notifyError } from '@/lib/notify';
import { notifications } from '@mantine/notifications';
import { getSessionId, getStoredStats, saveStats } from '@/lib/matching';
import { useUser } from '@/context/UserContext';
import { reportError, trackEvent } from '@/lib/telemetry';
import { legoProductUrl, affiliateRel } from '@/lib/affiliate';

// Productevents bevatten bewust alleen de soort handeling en geaggregeerde
// aantallen. Set-, step-, sessie- en element-ID's horen niet in analytics.
function trackMatchError(operation, error, { report = false } = {}) {
  trackEvent('Match Error', { operation });
  if (report) reportError(error, { source: `matchparts-${operation}` });
}

// Hint voor anonieme matchers: met een account blijven bijdragen bewaard
// over apparaten heen en verdien je badges. Verdwijnt zodra je ingelogd bent.
function AccountHint() {
  const { account, loading } = useUser();
  if (loading || account) return null;
  return (
    <Paper p="sm" radius="lg" style={{ backgroundColor: '#e7f5ff', border: '1px solid #d0ebff' }}>
      <Group justify="space-between" gap="sm" wrap="wrap">
        <Text size="sm" c="dark">
          💡 Your matches are saved anonymously on this device.{' '}
          <Text component={Link} href="/account/login" fw={600} c="blue" td="underline" size="sm">
            Log in
          </Text>{' '}
          to claim them, keep them across devices and earn badges.
        </Text>
      </Group>
    </Paper>
  );
}

const IMAGES_URL = process.env.NEXT_PUBLIC_IMAGES_URL || '/images';

// De inventory-picker kan 1000+ onderdelen bevatten; alles in één keer renderen
// duurt op tragere toestellen seconden. Render daarom eerst een klein aantal en
// breid uit zodra de gebruiker de grid richting het einde scrollt.
const INV_RENDER_INITIAL = 60;
const INV_RENDER_STEP = 120;

const mobileStyles = `
  .inv-mobile { display: none !important; }
  @media (max-width: 600px) {
    .match-card-layout {
      flex-direction: column !important;
    }
    .match-card-layout.match-card-unmatched {
      flex-direction: column-reverse !important;
    }
    .match-card-layout > .match-side-left {
      border-right: none !important;
    }
    .match-card-layout > .match-side-right {
      border-right: none !important;
      flex-basis: auto !important;
    }
    .match-approve-btn {
      padding: 8px 14px !important;
      font-size: 14px !important;
      min-height: 34px !important;
    }
    .inv-desktop { display: none !important; }
    .inv-mobile { display: block !important; }
    .match-card-layout > .match-side-left {
      flex-basis: auto !important;
    }
  }
  .sticky-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background-color: #fff;
    border-top: 1px solid #e9ecef;
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  }
  .sticky-actions-spacer {
    height: 64px;
  }
  /* De footer van de site staat in de paginaflow: houd hem vrij van de actiebalk */
  .site-footer {
    padding-bottom: calc(96px + env(safe-area-inset-bottom));
  }
`;

function imgUrl(path) {
  if (!path) return '';
  // Absolute URLs (LEGO CDN render-referenties) gaan ongewijzigd door
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/images')) return IMAGES_URL + path.slice(7);
  return IMAGES_URL + '/' + path;
}

// Render-referenties van de eigen server als WebP-thumbnail opvragen (zelfde
// contract als getThumbUrl in lib/imageUrl); LEGO CDN-plaatjes zijn al 192px.
function renderImgUrl(path, width = 96) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const resolved = imgUrl(path);
  return /\.(png|jpe?g)$/i.test(resolved) ? `${resolved}.${width}.webp` : resolved;
}

// PDF-crop uit het boekje + hoge-kwaliteit referentie (server-render of LEGO
// CDN-foto) naast elkaar. De crop blijft leidend - dát is wat het boekje toont
// en waar de stemmer de step tegen vergelijkt; de render is context zodat
// meteen duidelijk is om wélk onderdeel het gaat. Geen render beschikbaar of
// de CDN 404't: dan stilletjes alleen de crop, precies de oude weergave.
function PartImageDuo({ cropUrl, renderUrl, alt, maxSize = 170 }) {
  const [renderFailed, setRenderFailed] = useState(false);
  const showRender = !!renderUrl && !renderFailed;
  // Vult de beschikbare breedte van het paneel: beide vakken flexen mee en
  // blijven vierkant (aspect-ratio); maxSize begrenst ze op brede schermen.
  const colStyle = { flex: '1 1 0', maxWidth: maxSize, minWidth: 0 };
  const boxStyle = {
    width: '100%',
    aspectRatio: '1 / 1',
    borderRadius: 12,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    border: '2px solid #e9ecef',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  };
  const labelStyle = { fontSize: 9, letterSpacing: 0.4, lineHeight: 1 };
  const imgStyle = { width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' };
  return (
    <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'center', width: '100%' }}>
      <Stack gap={4} align="center" style={colStyle}>
        <Box style={boxStyle}>
          <img src={imgUrl(cropUrl)} alt={alt} decoding="async" style={imgStyle} />
        </Box>
        {showRender && (
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={labelStyle}>Booklet</Text>
        )}
      </Stack>
      {showRender && (
        <Stack gap={4} align="center" style={colStyle}>
          <Box style={boxStyle}>
            <img
              key={renderUrl}
              src={renderImgUrl(renderUrl, 192)}
              alt=""
              aria-hidden="true"
              decoding="async"
              onError={() => setRenderFailed(true)}
              style={imgStyle}
            />
          </Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={labelStyle}>Reference</Text>
        </Stack>
      )}
    </Box>
  );
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ActivityChart({ activity }) {
  if (!activity || activity.length === 0) return null;
  const maxVotes = Math.max(...activity.map(d => d.votes), 1);
  return (
    <Box ta="center">
      <Group justify="center" gap={6} align="flex-end" style={{ height: 60 }}>
        {activity.map((day, i) => {
          const isToday = i === activity.length - 1;
          const height = Math.max(4, (day.votes / maxVotes) * 48);
          const date = new Date(day.date);
          const label = isToday ? 'Today' : DAY_LABELS[date.getDay()];
          return (
            <Stack key={day.date} gap={2} align="center" style={{ width: 36, cursor: 'default' }}
              title={day.votes > 0 ? `${day.votes} parts by ${day.contributors} contributor${day.contributors !== 1 ? 's' : ''}` : 'No activity'}
            >
              {day.votes > 0 && (
                <Text size="xs" c={isToday ? '#228be6' : 'dimmed'} fw={isToday ? 600 : 400} style={{ fontSize: 9 }}>
                  {day.votes}
                </Text>
              )}
              <Box
                style={{
                  width: 24,
                  height,
                  borderRadius: 4,
                  backgroundColor: isToday ? '#228be6' : '#dee2e6',
                  transition: 'height 0.3s ease',
                }}
              />
              <Text size="xs" c={isToday ? '#228be6' : 'dimmed'} fw={isToday ? 600 : 400} style={{ fontSize: 9 }}>
                {label}
              </Text>
            </Stack>
          );
        })}
      </Group>
    </Box>
  );
}

function Leaderboard({ leaderboard, period, onPeriodChange }) {
  if (!leaderboard || leaderboard.length === 0) return null;
  const periodLabels = { day: 'Today', week: 'This week', month: 'This month' };
  const maxVotes = leaderboard[0]?.votes || 1;
  return (
    <Paper p="md" radius="lg" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={600} c="dark">Top contributors</Text>
        <Group gap={4}>
          {['day', 'week', 'month'].map(p => (
            <Badge
              key={p}
              size="xs"
              radius="sm"
              variant={period === p ? 'filled' : 'light'}
              color={period === p ? 'blue' : 'gray'}
              style={{ cursor: 'pointer', textTransform: 'none' }}
              onClick={() => onPeriodChange(p)}
            >
              {periodLabels[p]}
            </Badge>
          ))}
        </Group>
      </Group>
      <Stack gap={2}>
        {leaderboard.map((entry, i) => (
          <Fragment key={entry.rank}>
            {i > 0 && entry.rank > leaderboard[i - 1].rank + 1 && (
              <Box ta="center" py={2}>
                <Text size="xs" c="dimmed">···</Text>
              </Box>
            )}
            <Group
              gap={8}
              wrap="nowrap"
              style={{
                backgroundColor: entry.isYou ? '#e7f5ff' : 'transparent',
                borderRadius: 6,
                padding: '6px 10px',
              }}
            >
            <Text size="xs" fw={600} c={entry.isYou ? '#228be6' : 'dimmed'} style={{ width: 24, textAlign: 'center', flexShrink: 0 }}>
              {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
            </Text>
            {entry.name && (
              <Text size="xs" fw={entry.isYou ? 700 : 500} c={entry.isYou ? '#228be6' : '#495057'} truncate style={{ maxWidth: 110, flexShrink: 0 }}>
                {entry.name}
              </Text>
            )}
            <Box style={{ flex: 1, height: 8, backgroundColor: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}
              title={`${entry.votes} parts verified`}
            >
              <Box style={{ height: '100%', width: `${Math.max(2, (entry.votes / maxVotes) * 100)}%`, backgroundColor: entry.isYou ? '#228be6' : '#adb5bd', borderRadius: 4 }} />
            </Box>
            <Text size="xs" fw={entry.isYou ? 700 : 500} c={entry.isYou ? '#228be6' : '#495057'} style={{ minWidth: 32, textAlign: 'right', flexShrink: 0 }}>
              {entry.votes} <Text component="span" c="dimmed" inherit>parts</Text>
            </Text>
            {entry.isYou && (
              <Badge size="xs" variant="light" color="blue" radius="sm" style={{ flexShrink: 0 }}>you</Badge>
            )}
          </Group>
          </Fragment>
        ))}
      </Stack>
    </Paper>
  );
}

function ProgressSection({ progress, currentSetId }) {
  if (!progress || progress.length === 0) return null;
  return (
    <Paper p="md" radius="lg" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
      <Text size="sm" fw={600} c="dark" mb="sm">Active sets</Text>
      <Stack gap={8}>
        {progress.map(set => {
          const total = set.total_steps || 1;
          const verified = parseInt(set.verified_steps) || 0;
          const matched = parseInt(set.matched_steps) || 0;
          const pct = Math.round((verified / total) * 100);
          const isCurrent = set.set_id === currentSetId;
          return (
            <Box key={set.set_id}>
              <Group justify="space-between" mb={2}>
                <Group gap={6}>
                  {isCurrent && <Box style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#228be6' }} />}
                  <Text size="xs" fw={isCurrent ? 700 : 400} c={isCurrent ? 'dark' : 'dimmed'}>
                    {set.set_name || set.set_id}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed">{verified}/{total} verified ({pct}%)</Text>
              </Group>
              <Box style={{ height: 4, backgroundColor: '#dee2e6', borderRadius: 2, overflow: 'hidden' }}>
                <Box style={{ height: '100%', width: `${pct}%`, backgroundColor: pct === 100 ? '#40c057' : '#228be6', borderRadius: 2, transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function MatchPartsPage() {
  const { showNewBadges } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejectedSteps, setRejectedSteps] = useState(new Set());
  // Steps gemeld als "can't tell / cut off" (sluit wrong uit en andersom)
  const [unclearSteps, setUnclearSteps] = useState(new Set());
  const [sessionId, setSessionId] = useState('');
  const [stats, setStats] = useState({ totalReviewed: 0, streak: 0, bestStreak: 0 });
  const [showSuccess, setShowSuccess] = useState(false);
  const [invSearch, setInvSearch] = useState('');
  const [invRenderLimit, setInvRenderLimit] = useState(INV_RENDER_INITIAL);
  // Picker-weergave: 'crop' = PDF-crops uit het boekje (default, matcht de
  // step-afbeelding), 'render' = hoge-kwaliteit referenties. Bewust niet per
  // kaart gereset: wie renders prettiger vindt, houdt ze de hele sessie.
  const [invImageMode, setInvImageMode] = useState('crop');
  const [progress, setProgress] = useState([]);
  const [todayVerified, setTodayVerified] = useState(0);
  const [todayContributors, setTodayContributors] = useState(0);
  const [activeBuilders, setActiveBuilders] = useState(0);
  const [activity, setActivity] = useState([]);
  const [lastAction, setLastAction] = useState(null);
  const [flagConfirmOpen, setFlagConfirmOpen] = useState(false);
  const [sessionParts, setSessionParts] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPeriod, setLbPeriod] = useState('day');
  const sessionStartedRef = useRef(false);
  const sessionCompletedRef = useRef(false);

  const loadLeaderboard = useCallback((sid, period) => {
    if (!sid) return;
    fetchLeaderboard(sid, period).then(setLeaderboard).catch(() => {});
  }, []);

  useEffect(() => {
    const sid = getSessionId();
    const storedStats = getStoredStats();
    setSessionId(sid);
    setStats(storedStats);
    if (!sessionStartedRef.current) {
      sessionStartedRef.current = true;
      trackEvent('Match Session Started', {
        visitor: storedStats.totalReviewed > 0 ? 'returning' : 'new',
      });
    }
    fetchCommunityProgress().then(data => {
      setProgress(data.sets || data);
      setTodayVerified(data.today_verified || 0);
      setTodayContributors(data.today_contributors || 0);
      setActiveBuilders(data.active_builders || 0);
      setActivity(data.activity || []);
    }).catch(() => {});
    loadLeaderboard(sid, lbPeriod);
  }, []);

  // Auto-dismiss undo after 20 seconds
  useEffect(() => {
    if (!lastAction) return;
    const timer = setTimeout(() => setLastAction(null), 20000);
    return () => clearTimeout(timer);
  }, [lastAction]);


  const incrementStats = useCallback(() => {
    setStats(prev => {
      const next = {
        totalReviewed: prev.totalReviewed + 1,
        streak: prev.streak + 1,
        bestStreak: Math.max(prev.bestStreak, prev.streak + 1),
      };
      saveStats(next);
      return next;
    });
  }, []);

  // Debounced refresh of progress + leaderboard after approve/reassign actions.
  // Rapid consecutive actions coalesce into a single refresh ~2.5s after the last one.
  const refreshTimerRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      fetchCommunityProgress().then(data => {
        setProgress(data.sets || data);
        setTodayVerified(data.today_verified || 0);
        setTodayContributors(data.today_contributors || 0);
        setActiveBuilders(data.active_builders || 0);
        setActivity(data.activity || []);
      }).catch(() => {});
      loadLeaderboard(sessionId, lbPeriod);
    }, 2500);
  }, [sessionId, lbPeriod, loadLeaderboard]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Groep met precies één step (en geen calibratiekaart): toon meteen de
  // "Which part is this?"-picker in plaats van verify. Goed antwoord = nog
  // steeds één tik (de AI-kandidaat staat vooraan met blauwe rand), fout
  // antwoord = meteen het juiste part kiezen i.p.v. eerst rejecten. De
  // backend normaliseert kies-de-AI-kandidaat naar een approve-stem.
  const toPickerIfSingleStep = async (result) => {
    if (!result || result.done || !result.group || result.group.steps?.length !== 1 || result.is_calibration) {
      return result;
    }
    const step = result.group.steps[0];
    const stepSetId = step.matching_set_id || result.matching_set_id;
    try {
      const invResult = await fetchMatchingInventory(stepSetId, step.step_name);
      const inventory = Array.isArray(invResult) ? invResult : invResult.inventory;
      const sortHints = Array.isArray(invResult) ? undefined : invResult.sort_hints;
      return {
        done: false,
        type: 'unmatched',
        verify_single: true,
        set_id: result.set_id,
        set_name: result.set_name,
        matching_set_id: stepSetId,
        phase: result.phase,
        // Bewaard voor de prefetch: hiermee sluiten we deze groep uit bij het
        // alvast ophalen van de volgende match.
        source_element_id: result.group.element_id,
        step: { step_name: step.step_name, image_url: step.image_url, quantity: step.quantity },
        inventory,
        sort_hints: sortHints,
      };
    } catch (err) {
      // Inventory laden mislukt → val terug op de gewone verify-kaart
      trackMatchError('inventory_prefill', err, { report: true });
      return result;
    }
  };

  // Prefetch van de volgende match terwijl de huidige nog op scherm staat.
  // De huidige groep/step gaat als exclude-parameter mee (de vote erop is nog
  // niet gecast). Calibratie-items worden nooit geprefetcht: die zetten
  // server-side trap-state die door een tweede fetch overschreven zou worden.
  const prefetchRef = useRef(null);

  const preloadImages = (urls) => {
    urls.filter(Boolean).slice(0, 30).forEach((u) => {
      const im = new window.Image();
      im.src = imgUrl(u);
    });
  };

  const startPrefetch = (current) => {
    prefetchRef.current = null;
    if (!sessionId || !current || current.done) return;
    if (current.is_calibration || current.phase === 'calibration') return;
    // Zelfde modusverdeling als de live fetch (92% group / 8% unmatched)
    const preferGroup = Math.random() < 0.92;
    const excludeElement = current.group?.element_id || current.source_element_id || null;
    const isPlainUnmatched = current.type === 'unmatched' && !current.verify_single;
    const promise = (async () => {
      const result = preferGroup
        ? await fetchNextGroup(sessionId, excludeElement).catch(() => null)
        : await fetchNextUnmatched(
            sessionId,
            isPlainUnmatched ? current.matching_set_id : null,
            isPlainUnmatched ? current.step?.step_name : null
          ).catch(() => null);
      // "done" kan hier vals zijn (door de exclude); de consument valt dan
      // terug op een verse fetch die de definitieve staat bepaalt.
      if (!result || result.done) return null;
      const prepared = await toPickerIfSingleStep(result);
      // Warm de cache voor de plaatjes die de volgende kaart direct toont
      if (prepared.group) {
        preloadImages([prepared.group.image_url, ...prepared.group.steps.map((s) => s.image_url)]);
        if (prepared.group.render_url) {
          const im = new window.Image();
          im.src = renderImgUrl(prepared.group.render_url, 192);
        }
      } else if (prepared.step) {
        preloadImages([prepared.step.image_url]);
      }
      return prepared;
    })();
    prefetchRef.current = { promise, sessionId };
  };

  const loadNext = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setLoadError(null);
    setRejectedSteps(new Set());
    setUnclearSteps(new Set());
    setShowSuccess(false);
    setInvSearch('');
    setInvRenderLimit(INV_RENDER_INITIAL);
    let endpointErrorReported = false;
    try {
      // Geprefetchte match beschikbaar? Dan is de volgende kaart (vrijwel) instant.
      const prefetch = prefetchRef.current;
      prefetchRef.current = null;
      if (prefetch && prefetch.sessionId === sessionId) {
        const prefetched = await prefetch.promise;
        if (prefetched && !prefetched.done) {
          setData(prefetched);
          setLoading(false);
          return;
        }
      }
      // Decide the mode first (92% group / 8% unmatched), then fetch only that
      // endpoint; fall back to the other one if it comes back empty.
      const preferGroup = Math.random() < 0.92;
      const fetchPrimary = preferGroup ? fetchNextGroup : fetchNextUnmatched;
      const fetchFallback = preferGroup ? fetchNextUnmatched : fetchNextGroup;
      let primaryResult = null;
      let primaryError = null;
      try {
        primaryResult = await fetchPrimary(sessionId);
      } catch (err) {
        primaryError = err;
        endpointErrorReported = true;
        trackMatchError('load_primary', err, { report: true });
      }
      if (primaryResult && !primaryResult.done) {
        setData(await toPickerIfSingleStep(primaryResult));
      } else {
        let fallbackResult = null;
        let fallbackError = null;
        try {
          fallbackResult = await fetchFallback(sessionId);
        } catch (err) {
          fallbackError = err;
          endpointErrorReported = true;
          trackMatchError('load_fallback', err, { report: true });
        }
        if (fallbackResult && !fallbackResult.done) {
          setData(await toPickerIfSingleStep(fallbackResult));
        } else if (primaryError || fallbackError || !primaryResult || !fallbackResult) {
          // Een `done` uit één endpoint is geen bewijs dat al het werk klaar is
          // wanneer het andere endpoint niet bereikbaar was.
          throw primaryError || fallbackError || new Error('Invalid response while loading the next match');
        } else if (preferGroup) {
          setData(primaryResult); // group's "done" result (may carry a message)
        } else {
          setData(fallbackResult); // group's "done" result (may carry a message)
        }
      }
    } catch (err) {
      console.error('Failed to load:', err);
      setLoadError(err);
      // Endpoint-fouten zijn hierboven al gerapporteerd. Dit vangt ook fouten
      // bij het verwerken van een verder geldige response.
      if (!endpointErrorReported) {
        trackMatchError('load_response', err, { report: true });
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) loadNext();
  }, [sessionId, loadNext]);

  // Zodra een match op scherm staat: alvast de volgende klaarzetten
  useEffect(() => {
    if (data && !data.done) startPrefetch(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data?.done || loadError || sessionCompletedRef.current) return;
    sessionCompletedRef.current = true;
    trackEvent('Match Session Completed', {
      contributed: sessionParts > 0 ? 'yes' : 'no',
    });
  }, [data?.done, loadError, sessionParts]);

  const toggleReject = (key) => {
    setRejectedSteps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setUnclearSteps(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const toggleUnclear = (key) => {
    setUnclearSteps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setRejectedSteps(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const stepKeyToRef = (key) => {
    const colonIdx = key.indexOf(':');
    return { matching_set_id: parseInt(key.slice(0, colonIdx)), step_name: key.slice(colonIdx + 1) };
  };

  const handleApprove = async () => {
    if (submitting || !data) return;
    setSubmitting(true);
    setShowSuccess(true);
    try {
      // Convert rejected keys back to [{matching_set_id, step_name}] for cross-set
      const rejectedArray = Array.from(rejectedSteps).map(key => {
        const colonIdx = key.indexOf(':');
        if (colonIdx > -1) {
          return { matching_set_id: parseInt(key.slice(0, colonIdx)), step_name: key.slice(colonIdx + 1) };
        }
        return key; // legacy format
      });
      // Meldingen eerst: een bestaande sessie-stem houdt de step buiten approve-group
      if (unclearSteps.size > 0) {
        await reportUnclearSteps(Array.from(unclearSteps).map(stepKeyToRef), sessionId);
      }
      const approveResult = await approveGroup(data.matching_set_id, data.group.element_id, rejectedArray, sessionId, data.is_calibration);
      showNewBadges(approveResult?.newBadges);
      if (!data.is_calibration) {
        setLastAction({
          type: 'approve',
          steps: data.group.steps.map(s => ({ matching_set_id: s.matching_set_id, step_name: s.step_name })),
          previousData: data,
          timestamp: Date.now(),
        });
      }
      const approvedParts = data.group.steps.length - rejectedSteps.size - unclearSteps.size;
      trackEvent('Match Vote', {
        mode: data.is_calibration ? 'calibration' : 'group',
        result: rejectedSteps.size + unclearSteps.size === 0 ? 'approved' : approvedParts === 0 ? 'rejected' : 'mixed',
        reviewed: data.group.steps.length,
      });
      if (unclearSteps.size > 0) {
        trackEvent('Match Vote', { mode: 'group', result: 'unclear', reviewed: unclearSteps.size });
      }
      setSessionParts(prev => prev + approvedParts);
      // Refresh progress and leaderboard (debounced across rapid actions)
      scheduleRefresh();
      incrementStats();

      // Single-step group fully rejected: show inventory picker for this step immediately
      if (data.group.steps.length === 1 && rejectedSteps.size === 1) {
        const step = data.group.steps[0];
        const stepSetId = step.matching_set_id || data.matching_set_id;
        try {
          // step_name meegeven zodat de picker AI-kandidaten + votes als sort_hints krijgt
          const invResult = await fetchMatchingInventory(stepSetId, step.step_name);
          const inventory = Array.isArray(invResult) ? invResult : invResult.inventory;
          const sortHints = Array.isArray(invResult) ? undefined : invResult.sort_hints;
          setData({
            done: false,
            type: 'unmatched',
            set_id: data.set_id,
            set_name: data.set_name,
            matching_set_id: stepSetId,
            step: {
              step_name: step.step_name,
              image_url: step.image_url,
              quantity: step.quantity,
            },
            inventory,
            sort_hints: sortHints,
          });
          setRejectedSteps(new Set());
          setUnclearSteps(new Set());
          setShowSuccess(false);
          setInvSearch('');
          setInvRenderLimit(INV_RENDER_INITIAL);
          setSubmitting(false);
          return;
        } catch (err) {
          console.error('Failed to load inventory for reassign:', err);
          trackMatchError('inventory_reassign', err, { report: true });
        }
      }

      await loadNext();
      setSubmitting(false);
    } catch (err) {
      console.error('Approve failed:', err);
      trackMatchError('vote_group', err);
      notifyError(err, { title: 'Failed to submit your vote' });
      setSubmitting(false);
      setShowSuccess(false);
    }
  };

  const handleFlagInventory = () => {
    if (submitting || !data) return;
    setFlagConfirmOpen(true);
  };

  const confirmFlagInventory = async () => {
    if (submitting || !data) return;
    setFlagConfirmOpen(false);
    setSubmitting(true);
    try {
      const result = await flagInventory(data.matching_set_id, data.group.element_id, sessionId);
      trackEvent('Match Vote', {
        mode: 'group',
        result: 'inventory_image_reported',
        reviewed: 1,
      });
      notifications.show({
        title: result.flagged ? 'Image flagged for review' : 'Image report recorded',
        message: `${result.report_count} of ${result.threshold} reports for this image.`,
        color: result.flagged ? 'red' : 'blue',
      });
      setLastAction({
        type: 'flag',
        matchingSetId: data.matching_set_id,
        elementId: data.group.element_id,
        previousData: data,
        timestamp: Date.now(),
      });
      incrementStats();
      setTimeout(() => { loadNext(); setSubmitting(false); }, 300);
    } catch (err) {
      console.error('Flag failed:', err);
      trackMatchError('vote_flag', err);
      notifyError(err, { title: 'Failed to flag this part' });
      setSubmitting(false);
    }
  };

  const handleSelectInventory = async (elementId) => {
    if (submitting || !data) return;
    setSubmitting(true);
    setShowSuccess(true);
    try {
      const reassignResult = await reassignStep(data.matching_set_id, data.step.step_name, elementId, sessionId);
      trackEvent('Match Vote', {
        mode: data.verify_single ? 'single_verify' : 'unmatched',
        result: elementId === '__NOT_IN_INVENTORY__' ? 'not_in_inventory' : 'selected',
        reviewed: 1,
      });
      showNewBadges(reassignResult?.newBadges);
      setLastAction({
        type: 'reassign',
        matchingSetId: data.matching_set_id,
        stepNames: [data.step.step_name],
        previousData: data,
        timestamp: Date.now(),
      });
      setSessionParts(prev => prev + 1);
      scheduleRefresh();
      incrementStats();
      loadNext();
      setSubmitting(false);
    } catch (err) {
      console.error('Select failed:', err);
      trackMatchError('vote_select', err);
      notifyError(err, { title: 'Failed to submit your match' });
      setSubmitting(false);
      setShowSuccess(false);
    }
  };

  const handleReportUnclear = async () => {
    if (submitting || !data?.step) return;
    setSubmitting(true);
    try {
      await reportUnclearSteps([{ matching_set_id: data.matching_set_id, step_name: data.step.step_name }], sessionId);
      trackEvent('Match Vote', {
        mode: data.verify_single ? 'single_verify' : 'unmatched',
        result: 'unclear',
        reviewed: 1,
      });
      notifications.show({
        title: 'Thanks for reporting',
        message: 'Unclear images go to an admin after a few reports.',
        color: 'blue',
      });
      setLastAction({
        type: 'unclear',
        matchingSetId: data.matching_set_id,
        stepNames: [data.step.step_name],
        previousData: data,
        timestamp: Date.now(),
      });
      incrementStats();
      loadNext();
      setSubmitting(false);
    } catch (err) {
      console.error('Report unclear failed:', err);
      trackMatchError('vote_unclear', err);
      notifyError(err, { title: 'Failed to report this image' });
      setSubmitting(false);
    }
  };

  const handleUndo = async () => {
    if (!lastAction) return;
    try {
      await undoVotes(
        lastAction.matchingSetId,
        lastAction.type,
        lastAction.stepNames,
        sessionId,
        lastAction.elementId,
        lastAction.originalAiMatches,
        lastAction.steps, // cross-set steps [{matching_set_id, step_name}]
      );
      trackEvent('Match Undo', { action: lastAction.type || 'unknown' });
      setStats(prev => {
        const next = {
          totalReviewed: Math.max(0, prev.totalReviewed - 1),
          streak: Math.max(0, prev.streak - 1),
          bestStreak: prev.bestStreak,
        };
        saveStats(next);
        return next;
      });
      // Reverse session parts count for matching actions, not image reports.
      if (lastAction.type !== 'flag' && lastAction.type !== 'unclear' && lastAction.previousData?.group) {
        const undoParts = lastAction.previousData.group.steps?.length || 1;
        setSessionParts(prev => Math.max(0, prev - undoParts));
      } else if (lastAction.type !== 'flag' && lastAction.type !== 'unclear') {
        setSessionParts(prev => Math.max(0, prev - 1));
      }
      // Restore previous card
      if (lastAction.previousData) {
        setData(lastAction.previousData);
        setRejectedSteps(new Set());
        setUnclearSteps(new Set());
        setShowSuccess(false);
        setInvSearch('');
      }
      setLastAction(null);
    } catch (err) {
      console.error('Undo failed:', err);
      trackMatchError('undo', err);
      notifyError(err, { title: 'Failed to undo' });
    }
  };

  const handleSkip = () => {
    if (submitting) return;
    trackEvent('Match Skip', {
      mode: data?.type === 'unmatched' ? (data.verify_single ? 'single_verify' : 'unmatched') : 'group',
    });
    setLastAction(null);
    loadNext();
  };

  // Filtered + sorted inventory for the unmatched-step picker.
  // Memoized so it doesn't re-run on unrelated re-renders, with O(1) index
  // lookups for community votes and AI candidates instead of indexOf in the comparator.
  const filteredInventory = useMemo(() => {
    if (data?.type !== 'unmatched' || !Array.isArray(data.inventory)) return [];
    const hints = data.sort_hints || {};
    const communityVotes = hints.community_votes || [];
    const aiCandidates = hints.ai_candidates || [];
    // Keep the FIRST index for duplicate ids, matching indexOf() semantics
    const cvIndex = new Map();
    communityVotes.forEach((id, i) => { if (!cvIndex.has(id)) cvIndex.set(id, i); });
    const aiIndex = new Map();
    aiCandidates.forEach((id, i) => { if (!aiIndex.has(id)) aiIndex.set(id, i); });

    // Tokenize: collapse "1 x 4" → "1x4" in both query and field values
    const normalize = (s) => s ? s.toLowerCase().replace(/(\d)\s*x\s*(\d)/gi, '$1x$2') : '';
    const qTokens = normalize(invSearch).split(/\s+/).filter(w => w.length > 0);

    return data.inventory
      .filter(inv => {
        if (!invSearch) return true;
        // All query tokens must appear in at least one field
        const fields = [inv.element_id, inv.bl_part_id, inv.color_name, inv.part_name].map(normalize).join(' ');
        return qTokens.every(t => fields.includes(t));
      })
      .sort((a, b) => {
        // 1. Community voted items first
        const cvA = cvIndex.has(a.element_id) ? cvIndex.get(a.element_id) : -1;
        const cvB = cvIndex.has(b.element_id) ? cvIndex.get(b.element_id) : -1;
        const hasCvA = cvA !== -1 ? 0 : 1;
        const hasCvB = cvB !== -1 ? 0 : 1;
        if (hasCvA !== hasCvB) return hasCvA - hasCvB;
        if (hasCvA === 0 && hasCvB === 0) return cvA - cvB;

        // 2. AI candidates (top 4 after the wrong #1)
        const aiA = aiIndex.has(a.element_id) ? aiIndex.get(a.element_id) : -1;
        const aiB = aiIndex.has(b.element_id) ? aiIndex.get(b.element_id) : -1;
        const hasAiA = aiA !== -1 ? 0 : 1;
        const hasAiB = aiB !== -1 ? 0 : 1;
        if (hasAiA !== hasAiB) return hasAiA - hasAiB;
        if (hasAiA === 0 && hasAiB === 0) return aiA - aiB;

        // 3. Items with remaining > 0
        const ra = a.remaining > 0 ? 0 : 1;
        const rb = b.remaining > 0 ? 0 : 1;
        if (ra !== rb) return ra - rb;

        // 4. Sort by color (items without color last)
        const ca = a.color_name || 'zzz';
        const cb = b.color_name || 'zzz';
        if (ca !== cb) return ca.localeCompare(cb);

        // 5. Element ID alphabetically
        return (a.element_id || '').localeCompare(b.element_id || '');
      })
      // Markeer AI-kandidaten zodat de picker ze visueel kan onderscheiden
      .map(inv => ({ ...inv, _isAiCandidate: aiIndex.has(inv.element_id) }));
  }, [data?.inventory, data?.sort_hints, data?.type, invSearch]);

  // Loading state - skeleton shaped like the match card, no spinner flash
  if (loading) {
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Group justify="center">
            <Skeleton height={24} width={220} radius="xl" />
          </Group>
          <Paper
            p={0}
            radius="lg"
            style={{ backgroundColor: '#fff', border: '1px solid var(--bpb-border)', overflow: 'hidden' }}
          >
            <Skeleton height={280} radius={0} />
            <Box p="md">
              <Skeleton height={14} width="45%" radius="xl" mb={12} />
              <Group gap="sm">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={72} width={72} radius="md" />
                ))}
              </Group>
            </Box>
          </Paper>
          <Group justify="center">
            <Skeleton height={36} width={130} radius="xl" />
            <Skeleton height={36} width={130} radius="xl" />
          </Group>
        </Stack>
      </Container>
    );
  }

  // Een laadfout is nadrukkelijk iets anders dan een lege werkvoorraad. De
  // inline alert blijft zichtbaar en bedienbaar voor toetsenbord/screenreader.
  if (loadError) {
    return (
      <Container size="sm" py="xl">
        <Paper
          role="alert"
          aria-live="assertive"
          p="xl"
          radius="lg"
          style={{ backgroundColor: '#fff5f5', border: '1px solid #ffc9c9' }}
        >
          <Stack align="center" gap="md" ta="center">
            <IconAlertTriangle size={44} color="#e03131" stroke={1.7} aria-hidden="true" />
            <Box>
              <Title order={2} mb={6} style={{ color: '#212529' }}>Couldn&apos;t load the next match</Title>
              <Text c="dimmed" maw={460}>
                We couldn&apos;t confirm whether there are parts left to review. Check your connection and try again.
              </Text>
            </Box>
            <Button color="red" leftSection={<IconRefresh size={16} />} onClick={loadNext} style={{ backgroundColor: '#c92a2a' }}>
              Try again
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // All done state
  if (!data || data.done) {
    return (
      <Container size="md" py="xl">
        <Stack gap="xl">
          <Box ta="center">
            <Group justify="center" mb="md">
              <IconStar size={48} color="#fab005" stroke={1.5} />
            </Group>
            <Title order={1} mb="xs" style={{ color: '#212529' }}>All done!</Title>
            <Text c="dimmed" size="lg" maw={420} mx="auto">
              {data?.message || 'No parts to review right now. Check back later!'}
            </Text>
          </Box>

          {stats.totalReviewed > 0 && (
            <Paper
              p="xl"
              radius="lg"
              style={{ backgroundColor: '#fff', border: '1px solid #e9ecef', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}
            >
              <Group justify="center" gap="xl">
                <Stack align="center" gap={4}>
                  <Text size="xl" fw={800} style={{ color: '#2f9e44' }}>{stats.totalReviewed}</Text>
                  <Text size="xs" c="dimmed">Parts reviewed</Text>
                </Stack>
                {stats.bestStreak > 1 && (
                  <Stack align="center" gap={4}>
                    <Group gap={4}>
                      <IconFlame size={20} color="#fd7e14" />
                      <Text size="xl" fw={800} style={{ color: '#fd7e14' }}>{stats.bestStreak}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">Best streak</Text>
                  </Stack>
                )}
              </Group>
            </Paper>
          )}

          <ProgressSection progress={progress} />
        </Stack>
      </Container>
    );
  }

  // Unmatched step mode
  if (data.type === 'unmatched') {
    const filtered = filteredInventory;

    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          {/* Header */}
          <Box ta="center">
            <Group justify="center" gap="sm" align="center">
              <Box className="feature-icon-bubble" style={{ backgroundColor: '#fff4e6', width: 38, height: 38, marginBottom: 0, borderRadius: 11 }}>
                <IconPuzzle size={20} color="#e8590c" stroke={1.7} />
              </Box>
              <Title order={3} style={{ color: 'var(--bpb-text)', fontWeight: 800 }}>Which part is this?</Title>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>
              Every vote helps map a set · <a href="/contribute" style={{ color: 'var(--bpb-blue)', textDecoration: 'none', fontWeight: 600 }}>Learn more</a>
            </Text>
            {data.verify_single && (
              <Text size="xs" c="dimmed" mt={2}>
                AI already has a suggestion: the <Text component="span" fw={600} style={{ color: 'var(--bpb-blue)' }}>blue-bordered</Text> part. Tap it to confirm, or pick the right one.
              </Text>
            )}
          </Box>

          {/* Main card */}
          <Paper
            p={0}
            radius="lg"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #e9ecef',
              boxShadow: showSuccess ? '0 0 0 3px #d3f9d8' : '0 4px 12px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
            }}
          >
            <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
            <Box className="match-card-layout match-card-unmatched" style={{ display: 'flex', flexWrap: 'wrap' }}>
              {/* Inventory picker - left */}
              <Box className="match-side-left" style={{ padding: 14, flex: '1 1 350px', maxWidth: '100%', borderRight: '1px solid #e9ecef' }}>
                <Group justify="space-between" align="center" gap="xs" mb="sm" wrap="nowrap">
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Select the matching part
                  </Text>
                  {filtered.some(inv => inv.render_url) && (
                    <SegmentedControl
                      size="xs"
                      radius="xl"
                      value={invImageMode}
                      onChange={setInvImageMode}
                      aria-label="Part image style"
                      data={[
                        { label: 'Booklet', value: 'crop' },
                        { label: 'Render', value: 'render' },
                      ]}
                      styles={{ label: { fontSize: 11, padding: '3px 10px' } }}
                    />
                  )}
                </Group>
                <TextInput
                  aria-label="Filter matching parts"
                  aria-controls="matching-inventory-grid"
                  aria-describedby="matching-inventory-status"
                  placeholder="Search by number, name, or color..."
                  size="sm"
                  mb="sm"
                  radius="xl"
                  leftSection={<IconSearch size={16} aria-hidden="true" style={{ color: '#66707a' }} />}
                  value={invSearch}
                  onChange={(e) => { setInvSearch(e.target.value); setInvRenderLimit(INV_RENDER_INITIAL); }}
                  rightSection={invSearch ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      radius="xl"
                      size="sm"
                      aria-label="Clear part filter"
                      onClick={() => { setInvSearch(''); setInvRenderLimit(INV_RENDER_INITIAL); }}
                    >
                      <IconX size={14} aria-hidden="true" />
                    </ActionIcon>
                  ) : null}
                  rightSectionPointerEvents="all"
                  classNames={{ input: 'form-input' }}
                />
                <span id="matching-inventory-status" className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                  {`${filtered.length} matching ${filtered.length === 1 ? 'part' : 'parts'} available.`}
                </span>
                <Box
                  id="matching-inventory-grid"
                  aria-label="Parts in this set"
                  style={{ maxHeight: 400, overflowY: 'auto' }}
                  onScroll={(e) => {
                    // Grid groeit mee met het scrollen i.p.v. alles vooraf te renderen
                    const el = e.currentTarget;
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300 && invRenderLimit < filtered.length) {
                      setInvRenderLimit((l) => l + INV_RENDER_STEP);
                    }
                  }}
                >
                  <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6 }}>
                    {filtered.slice(0, invRenderLimit).map(inv => (
                      <Box
                        key={inv.element_id}
                        component="button"
                        type="button"
                        aria-label={`Select part ${inv.element_id}${inv._isAiCandidate ? ', AI suggestion' : ''}`}
                        onClick={() => handleSelectInventory(inv.element_id)}
                        style={{
                          position: 'relative',
                          padding: 4,
                          borderRadius: 10,
                          border: inv._isAiCandidate ? '2px solid #74c0fc' : '2px solid #e9ecef',
                          backgroundColor: inv._isAiCandidate ? '#f1f8ff' : '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center',
                          width: '100%',
                          color: 'inherit',
                          font: 'inherit',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#228be6'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = inv._isAiCandidate ? '#74c0fc' : '#e9ecef'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        {inv._isAiCandidate && (
                          <Text
                            component="span"
                            style={{
                              position: 'absolute',
                              top: 3,
                              right: 3,
                              fontSize: 8,
                              fontWeight: 700,
                              lineHeight: 1,
                              padding: '2px 4px',
                              borderRadius: 6,
                              backgroundColor: '#228be6',
                              color: '#fff',
                              pointerEvents: 'none',
                            }}
                          >
                            AI
                          </Text>
                        )}
                        <img
                          key={invImageMode === 'render' && inv.render_url ? 'render' : 'crop'}
                          src={invImageMode === 'render' && inv.render_url
                            ? renderImgUrl(inv.render_url, 192)
                            : imgUrl(inv.image_url)}
                          alt={inv.element_id}
                          loading="lazy"
                          decoding="async"
                          onError={invImageMode === 'render' && inv.render_url ? (e) => {
                            // CDN-render bestaat niet → stilletjes terug naar de crop
                            if (e.currentTarget.dataset.fallback !== '1') {
                              e.currentTarget.dataset.fallback = '1';
                              e.currentTarget.src = imgUrl(inv.image_url);
                            }
                          } : undefined}
                          style={{ width: '100%', maxWidth: 120, aspectRatio: '1 / 1', objectFit: 'contain', mixBlendMode: 'multiply', display: 'block', margin: '0 auto' }}
                        />
                        <Text size="xs" c="dimmed" ta="center" style={{ fontSize: 9, lineHeight: 1.2, marginTop: 2 }}>{inv.element_id}</Text>
                      </Box>
                    ))}
                  </Box>
                  {filtered.length > invRenderLimit && (
                    <Text size="xs" c="dimmed" ta="center" mt={8}>
                      Showing {invRenderLimit} of {filtered.length} parts. Scroll for more, or search above
                    </Text>
                  )}
                </Box>
              </Box>

              {/* Step image - right on desktop, top on mobile */}
              <Box className="match-side-right" style={{ backgroundColor: '#f8f9fa', padding: 14, flex: '0 1 240px', maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Stack align="center" gap="sm">
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">Unknown part</Text>
                  <Box
                    style={{
                      width: 180,
                      height: 180,
                      borderRadius: 16,
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e9ecef',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    }}
                  >
                    <img
                      src={imgUrl(data.step.image_url)}
                      alt={data.step.step_name}
                      decoding="async"
                      style={{ width: 160, height: 160, objectFit: 'contain', mixBlendMode: 'multiply' }}
                    />
                  </Box>
                  <Button
                    type="button"
                    size="compact-sm"
                    variant="subtle"
                    color="red"
                    onClick={() => handleSelectInventory('__NOT_IN_INVENTORY__')}
                  >
                    Not in inventory
                  </Button>
                  <Button
                    type="button"
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconEyeOff size={14} />}
                    onClick={handleReportUnclear}
                    disabled={submitting}
                  >
                    Can&apos;t tell / cut off
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Paper>

          {/* Skip + Undo - sticky bottom bar */}
          <Box className="sticky-actions">
            <Group justify="center" gap="sm">
              <Button
                variant="subtle"
                color="gray"
                size="compact-sm"
                onClick={handleSkip}
                disabled={submitting}
                rightSection={<IconArrowRight size={14} />}
              >
                Skip
              </Button>
              {lastAction && (
                <Button
                  variant="light"
                  color="gray"
                  size="compact-sm"
                  onClick={handleUndo}
                  leftSection={<IconArrowBack size={14} />}
                  radius="md"
                >
                  Undo
                </Button>
              )}
            </Group>
          </Box>

          {/* Stats + community info */}
          <Box ta="center">
            {stats.totalReviewed > 0 && (
              <Paper p="xs" px="md" radius="xl" style={{ backgroundColor: '#f0f4ff', border: '1px solid #dbe4ff', display: 'inline-block' }}>
                <Group justify="center" gap="sm" wrap="nowrap">
                  <Group gap={4}>
                    <IconCheck size={14} color="#2f9e44" />
                    <Text size="sm" fw={600} c="dark">{stats.totalReviewed}</Text>
                    <Text size="xs" c="dimmed">votes</Text>
                  </Group>
                  {stats.streak > 1 && stats.streak !== stats.totalReviewed && (
                    <Group gap={4}>
                      <IconFlame size={14} color="#fd7e14" />
                      <Text size="sm" fw={600} c="dark">{stats.streak}</Text>
                      <Text size="xs" c="dimmed">streak</Text>
                    </Group>
                  )}
                  {sessionParts > 0 && (
                    <Group gap={4}>
                      <IconStar size={14} color="#fab005" />
                      <Text size="sm" fw={600} c="dark">{sessionParts}</Text>
                      <Text size="xs" c="dimmed">parts this session</Text>
                    </Group>
                  )}
                </Group>
              </Paper>
            )}
            {(activeBuilders >= 2 || todayVerified > 0) && (
              <Text size="xs" c="dimmed" mt={8}>
                {activeBuilders >= 2 && (
                  <Text component="span" style={{ color: '#2f9e44' }}>● </Text>
                )}
                {activeBuilders >= 2 && <><Text component="span" fw={600} c="dark">{activeBuilders}</Text> builders active{todayVerified > 0 && <> · </>}</>}
                {todayVerified > 0 && <><Text component="span" fw={600} c="dark">{todayVerified}</Text> parts verified today{todayContributors > 0 && <> by <Text component="span" fw={600} c="dark">{todayContributors}</Text> contributor{todayContributors !== 1 ? 's' : ''}</>}</>}
              </Text>
            )}
          </Box>

          {/* Active sets tags */}
          {progress.length > 0 && (
            <Group justify="center" gap="xs" wrap="wrap">
              {progress.map(set => {
                const total = set.total_steps || 1;
                const verified = parseInt(set.verified_steps) || 0;
                const pct = Math.round((verified / total) * 100);
                const isCurrent = set.set_id === data.set_id;
                return (
                  <Badge
                    key={set.set_id}
                    variant="light" size="sm" radius="sm"
                    style={{
                      backgroundColor: isCurrent ? '#e7f5ff' : '#f8f9fa',
                      color: isCurrent ? '#228be6' : '#868e96',
                      fontWeight: isCurrent ? 600 : 500,
                      border: `1px solid ${isCurrent ? '#a5d8ff' : '#e9ecef'}`,
                      textTransform: 'none',
                    }}
                  >
                    {set.set_id} - {set.set_name || set.set_id} - {pct}%
                  </Badge>
                );
              })}
            </Group>
          )}

          {/* How it works - shown for new users */}
          {stats.totalReviewed < 5 && (
            <Paper
              p="lg"
              radius="lg"
              style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
            >
              <Title order={4} mb="sm" style={{ color: '#495057' }}>
                How it works
              </Title>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">1.</Text> Look at the unknown part on the right
                </Text>
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">2.</Text> Use the search to filter by name, color, or dimensions (e.g. &quot;white 1x4&quot; or &quot;red plate&quot;)
                </Text>
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">3.</Text> Click the matching part from the inventory, or &quot;Not in inventory&quot; if it doesn&apos;t belong
                </Text>
              </Stack>
            </Paper>
          )}

          <AccountHint />

          {/* Activity + Leaderboard */}
          {(activity.length > 0 || leaderboard.length > 0) && (
            <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {activity.length > 0 && (
                <Box style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Paper p="md" radius="lg" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', height: '100%' }}>
                    <Text size="sm" fw={600} c="dark" mb="sm">Parts verified</Text>
                    <ActivityChart activity={activity} />
                  </Paper>
                </Box>
              )}
              {leaderboard.length > 0 && (
                <Box style={{ flex: '2 1 280px', minWidth: 0 }}>
                  <Leaderboard leaderboard={leaderboard} period={lbPeriod} onPeriodChange={(p) => { setLbPeriod(p); loadLeaderboard(sessionId, p); }} />
                </Box>
              )}
            </Box>
          )}
          <Box className="sticky-actions-spacer" />

        </Stack>
      </Container>
    );
  }

  // Group verification mode
  const { group } = data;
  const approvedCount = group.steps.length - rejectedSteps.size - unclearSteps.size;

  return (
    <Container size="md" py="xl">
      <Modal
        opened={flagConfirmOpen}
        onClose={() => setFlagConfirmOpen(false)}
        title="Report an incorrect inventory image?"
        centered
        size="sm"
      >
        <Text size="sm" c="dimmed" mb="lg">
          Four independent reports are needed before this set&apos;s inventory image is flagged for admin review. Your report will not change existing step matches.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setFlagConfirmOpen(false)}>Cancel</Button>
          <Button color="red" leftSection={<IconFlag size={16} />} onClick={confirmFlagInventory}>
            Report image
          </Button>
        </Group>
      </Modal>
      <Stack gap="md">
        {/* Header */}
        <Box ta="center">
          <Group justify="center" gap="sm" align="center">
            <Box className="feature-icon-bubble" style={{ backgroundColor: '#fff4e6', width: 38, height: 38, marginBottom: 0, borderRadius: 11 }}>
              <IconPuzzle size={20} color="#e8590c" stroke={1.7} />
            </Box>
            <Title order={3} style={{ color: 'var(--bpb-text)', fontWeight: 800 }}>Verify part matches</Title>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            Every vote helps map a set · <a href="/contribute" style={{ color: 'var(--bpb-blue)', textDecoration: 'none', fontWeight: 600 }}>Learn more</a>
          </Text>
        </Box>

        {/* Main card */}
        <Paper
          p={0}
          radius="lg"
          style={{
            backgroundColor: '#fff',
            border: '1px solid #e9ecef',
            boxShadow: showSuccess ? '0 0 0 3px #d3f9d8' : '0 4px 12px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            transition: 'box-shadow 0.3s ease',
          }}
        >
          <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
          <Box className="match-card-layout" style={{ display: 'flex', flexWrap: 'wrap' }}>
            {/* Inventory part - left on desktop, top on mobile */}
            <Box className="match-side-left" style={{ backgroundColor: '#f8f9fa', padding: 14, flex: '0 1 300px', maxWidth: '100%', borderRight: '1px solid #e9ecef' }}>
              {/* Desktop: vertical stack */}
              <Stack gap="sm" align="center" className="inv-desktop">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Inventory part</Text>
                <PartImageDuo
                  key={group.element_id}
                  cropUrl={group.image_url}
                  renderUrl={group.render_url}
                  alt={group.element_id}
                />
                <Stack gap={2} align="center">
                  <Text fw={700} size="sm" c="#212529">{group.bl_part_id || group.element_id}</Text>
                  {group.color_name && (
                    <Group gap={4} align="center">
                      {group.color_hex && (
                        <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: `#${group.color_hex}`, border: '1px solid rgba(0,0,0,0.15)' }} />
                      )}
                      <Text size="xs" c="dimmed">{group.color_name}</Text>
                    </Group>
                  )}
                  <Text size="xs" c="dimmed">{group.element_id}</Text>
                </Stack>
                {group.sets && group.sets.length > 1 && (
                  <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.4 }}>
                    Used in <Text component="span" fw={600} c="dark">{group.sets.length}</Text> sets:{' '}
                    {group.sets.slice(0, 3).map((s, i) => (
                      <span key={s.set_id}>
                        {i > 0 && ', '}
                        <a href={legoProductUrl(s.set_id)} target="_blank" rel={affiliateRel} style={{ color: '#228be6', textDecoration: 'none' }}>{s.set_id}</a>
                      </span>
                    ))}
                    {group.sets.length > 3 && (
                      <span
                        style={{ color: '#228be6', cursor: 'default' }}
                        title={group.sets.slice(3).map(s => `${s.set_id} - ${s.set_name || s.set_id}`).join('\n')}
                      > +{group.sets.length - 3} more</span>
                    )}
                  </Text>
                )}
                {!data.is_calibration && <Button
                  variant="subtle"
                  color="red"
                  size="compact-xs"
                  leftSection={<IconFlag size={12} />}
                  onClick={handleFlagInventory}
                  disabled={submitting}
                  style={{ fontSize: 10, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 }}
                >
                  Report wrong image
                </Button>}
              </Stack>
              {/* Mobile: horizontal layout */}
              <Box className="inv-mobile">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>Inventory part</Text>
                <Box style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <PartImageDuo
                  key={group.element_id}
                  cropUrl={group.image_url}
                  renderUrl={group.render_url}
                  alt={group.element_id}
                />
                <Stack gap={2}>
                  <Text fw={700} size="sm" c="#212529">{group.bl_part_id || group.element_id}</Text>
                  {group.color_name && (
                    <Group gap={4} align="center">
                      {group.color_hex && (
                        <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: `#${group.color_hex}`, border: '1px solid rgba(0,0,0,0.15)' }} />
                      )}
                      <Text size="xs" c="dimmed">{group.color_name}</Text>
                    </Group>
                  )}
                  <Text size="xs" c="dimmed">{group.element_id}</Text>
                  {!data.is_calibration && <Button
                    variant="subtle"
                    color="red"
                    size="compact-xs"
                    leftSection={<IconFlag size={12} />}
                    onClick={handleFlagInventory}
                    disabled={submitting}
                    style={{ fontSize: 10, whiteSpace: 'normal', lineHeight: 1.3, padding: 0, alignSelf: 'flex-start' }}
                  >
                    Report wrong image
                  </Button>}
                </Stack>
                </Box>
              </Box>
            </Box>

            {/* Steps grid - right on desktop, top on mobile */}
            <Box className="match-side-right" style={{ padding: 14, flex: '1 1 300px', maxWidth: '100%' }}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm">
                Matched steps: tap to mark as wrong
              </Text>
              <Text size="xs" c="dimmed" mt={-6} mb="sm">
                Image cut off or unreadable? Use <IconEyeOff size={12} style={{ verticalAlign: '-2px' }} aria-label="can't tell" /> instead.
              </Text>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {group.steps.map((step) => {
                  const stepKey = `${step.matching_set_id}:${step.step_name}`;
                  const isRejected = rejectedSteps.has(stepKey);
                  const isUnclear = unclearSteps.has(stepKey);
                  const isCrossSet = group.sets && group.sets.length > 1;
                  return (
                    <Box
                      key={stepKey}
                      onClick={() => toggleReject(stepKey)}
                      style={{
                        position: 'relative',
                        cursor: 'pointer',
                        padding: 4,
                        borderRadius: 10,
                        border: isRejected ? '2px solid #fa5252' : isUnclear ? '2px solid #fd7e14' : '2px solid #e9ecef',
                        backgroundColor: isRejected ? '#fff5f5' : isUnclear ? '#fff4e6' : '#f8f9fa',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => { if (!isRejected && !isUnclear) e.currentTarget.style.borderColor = '#adb5bd'; }}
                      onMouseLeave={(e) => { if (!isRejected && !isUnclear) e.currentTarget.style.borderColor = '#e9ecef'; }}
                    >
                      <img
                        src={imgUrl(step.image_url)}
                        alt={step.step_name}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: 84,
                          height: 84,
                          objectFit: 'contain',
                          mixBlendMode: 'multiply',
                          opacity: isRejected || isUnclear ? 0.3 : 1,
                          display: 'block',
                        }}
                      />
                      {isCrossSet && step.set_id && (
                        <Text size="xs" c="dimmed" ta="center" style={{ fontSize: 7, marginTop: 1 }}>{step.set_id}</Text>
                      )}
                      <ActionIcon
                        className="match-unclear-btn"
                        size={22}
                        radius="xl"
                        variant={isUnclear ? 'filled' : 'white'}
                        color={isUnclear ? 'orange' : 'gray'}
                        aria-label={isUnclear ? `Undo "can't tell" for this step` : `Can't tell: image cut off or unreadable`}
                        aria-pressed={isUnclear}
                        title="Can't tell: image cut off or unreadable"
                        onClick={(e) => { e.stopPropagation(); toggleUnclear(stepKey); }}
                        style={{ position: 'absolute', top: 2, right: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.15)', zIndex: 1 }}
                      >
                        <IconEyeOff size={12} />
                      </ActionIcon>
                      {isUnclear && (
                        <Text size="xs" fw={700} c="orange.8" ta="center" style={{ position: 'absolute', left: 0, right: 0, top: '40%', fontSize: 10, pointerEvents: 'none' }}>
                          Can&apos;t tell
                        </Text>
                      )}
                      {isRejected && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(250, 82, 82, 0.9)',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconX size={14} color="white" stroke={3} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              {rejectedSteps.size + unclearSteps.size > 0 ? (
                <Group gap="xs" mt="sm">
                  {rejectedSteps.size > 0 && (
                    <Badge size="lg" variant="light" color="red" radius="md">
                      {rejectedSteps.size} wrong
                    </Badge>
                  )}
                  {unclearSteps.size > 0 && (
                    <Badge size="lg" variant="light" color="orange" radius="md">
                      {unclearSteps.size} can&apos;t tell
                    </Badge>
                  )}
                </Group>
              ) : group.steps.length > 1 ? (
                <Text size="xs" c="dimmed" mt="sm" style={{ fontStyle: 'italic' }}>
                  Some items include a random wrong match to ensure quality.
                </Text>
              ) : null}
            </Box>
          </Box>
        </Paper>

        {/* Action buttons - sticky bottom bar */}
        <Box className="sticky-actions">
          <Group justify="space-between" maw={800} mx="auto" wrap="nowrap">
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              onClick={handleSkip}
              disabled={submitting}
              rightSection={<IconArrowRight size={14} />}
            >
              Skip
            </Button>
            <Group gap="sm" wrap="nowrap">
              {lastAction && (
                <Button
                  variant="light"
                  color="gray"
                  size="compact-sm"
                  onClick={handleUndo}
                  leftSection={<IconArrowBack size={14} />}
                  radius="md"
                >
                  Undo
                </Button>
              )}
              <Button
                className="match-approve-btn"
                color="green"
                size="md"
                onClick={handleApprove}
                disabled={submitting}
                loading={submitting}
                leftSection={<IconCheck size={16} />}
                radius="md"
                style={{ boxShadow: '0 4px 12px rgba(47, 158, 68, 0.25)' }}
              >
                {approvedCount > 0 ? `Approve ${approvedCount}` : 'Submit'}
              </Button>
            </Group>
          </Group>
        </Box>

        {/* Stats + community info */}
        <Box ta="center">
          {stats.totalReviewed > 0 && (
            <Paper p="xs" px="md" radius="xl" style={{ backgroundColor: '#f0f4ff', border: '1px solid #dbe4ff', display: 'inline-block' }}>
              <Group justify="center" gap="sm" wrap="nowrap">
                <Group gap={4}>
                  <IconCheck size={14} color="#2f9e44" />
                  <Text size="sm" fw={600} c="dark">{stats.totalReviewed}</Text>
                  <Text size="xs" c="dimmed">reviewed</Text>
                </Group>
                {stats.streak > 1 && stats.streak !== stats.totalReviewed && (
                  <Group gap={4}>
                    <IconFlame size={14} color="#fd7e14" />
                    <Text size="sm" fw={600} c="dark">{stats.streak}</Text>
                    <Text size="xs" c="dimmed">streak</Text>
                  </Group>
                )}
                {sessionParts > 0 && (
                  <Group gap={4}>
                    <IconStar size={14} color="#fab005" />
                    <Text size="sm" fw={600} c="dark">{sessionParts}</Text>
                    <Text size="xs" c="dimmed">parts this session</Text>
                  </Group>
                )}
              </Group>
            </Paper>
          )}
          {(activeBuilders >= 2 || todayVerified > 0) && (
            <Text size="xs" c="dimmed" mt={8}>
              {activeBuilders >= 2 && (
                <Text component="span" style={{ color: '#2f9e44' }}>● </Text>
              )}
              {activeBuilders >= 2 && <><Text component="span" fw={600} c="dark">{activeBuilders}</Text> builders active{todayVerified > 0 && <> · </>}</>}
              {todayVerified > 0 && <><Text component="span" fw={600} c="dark">{todayVerified}</Text> parts verified today{todayContributors > 0 && <> by <Text component="span" fw={600} c="dark">{todayContributors}</Text> contributor{todayContributors !== 1 ? 's' : ''}</>}</>}
            </Text>
          )}
        </Box>

        {/* Active sets tags */}
        {progress.length > 0 && (() => {
          const currentSetIds = new Set((data.group.sets || []).map(s => s.set_id));
          return (
            <Group justify="center" gap="xs" wrap="wrap">
              {[...progress].sort((a, b) => {
                const pctA = Math.round(((parseInt(a.verified_steps) || 0) / (a.total_steps || 1)) * 100);
                const pctB = Math.round(((parseInt(b.verified_steps) || 0) / (b.total_steps || 1)) * 100);
                return pctB - pctA;
              }).map(set => {
                const total = set.total_steps || 1;
                const verified = parseInt(set.verified_steps) || 0;
                const pct = Math.round((verified / total) * 100);
                const isCurrent = currentSetIds.has(set.set_id);
                return (
                  <Badge
                    key={set.set_id}
                    variant="light" size="sm" radius="sm"
                    style={{
                      backgroundColor: isCurrent ? '#e7f5ff' : '#f8f9fa',
                      color: isCurrent ? '#228be6' : '#868e96',
                      fontWeight: isCurrent ? 600 : 500,
                      border: `1px solid ${isCurrent ? '#a5d8ff' : '#e9ecef'}`,
                      textTransform: 'none',
                    }}
                  >
                    {set.set_id} - {set.set_name || set.set_id} - {pct}%
                  </Badge>
                );
              })}
            </Group>
          );
        })()}

        {/* How it works - shown for new users */}
        {stats.totalReviewed < 5 && (
          <Paper
            p="lg"
            radius="lg"
            style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
          >
            <Title order={4} mb="sm" style={{ color: '#495057' }}>
              How it works
            </Title>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                <Text component="span" fw={600} c="dark">1.</Text> Compare the inventory part (left) with the step images (right)
              </Text>
              <Text size="sm" c="dimmed">
                <Text component="span" fw={600} c="dark">2.</Text> Tap any step image that doesn&apos;t match to mark it as wrong
              </Text>
              <Text size="sm" c="dimmed">
                <Text component="span" fw={600} c="dark">3.</Text> Click Approve to confirm the remaining matches
              </Text>
            </Stack>
          </Paper>
        )}

        {/* Activity + Leaderboard */}
        {(activity.length > 0 || leaderboard.length > 0) && (
          <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {activity.length > 0 && (
              <Box style={{ flex: '1 1 200px', minWidth: 0 }}>
                <Paper p="md" radius="lg" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', height: '100%' }}>
                  <Text size="sm" fw={600} c="dark" mb="sm">Parts verified</Text>
                  <ActivityChart activity={activity} />
                </Paper>
              </Box>
            )}
            {leaderboard.length > 0 && (
              <Box style={{ flex: '2 1 280px', minWidth: 0 }}>
                <Leaderboard leaderboard={leaderboard} period={lbPeriod} onPeriodChange={(p) => { setLbPeriod(p); loadLeaderboard(sessionId, p); }} />
              </Box>
            )}
          </Box>
        )}
        <Box className="sticky-actions-spacer" />
      </Stack>
    </Container>
  );
}
